/**
 * AdminUserFormModal — dual-purpose modal:
 *   mode="create" → POST /api/admin/users
 *   mode="reset"  → POST /api/admin/users/{id}/reset-password
 *
 * Both flows generate a 12-char temp password when the admin leaves the
 * password field blank. The resulting plaintext is shown once with a
 * copy-to-clipboard button so it can be shared out-of-band.
 */
import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  X, UserPlus, KeyRound, Copy, Loader2, Eye, EyeOff, ShieldCheck,
} from 'lucide-react';

const AdminUserFormModal = ({ open, onClose, mode = 'create', targetUser = null, onDone }) => {
  const { API } = useContext(AuthContext);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState('free');
  const [password, setPassword] = useState('');
  const [autoGen, setAutoGen] = useState(true);   // reset-mode radio
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const reset = () => {
    setEmail(''); setDisplayName(''); setRole('free');
    setPassword(''); setAutoGen(true); setResult(null);
  };
  const close = () => { reset(); onClose && onClose(); };

  const submit = async (e) => {
    e && e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === 'create') {
        const payload = {
          email: email.trim(),
          role,
          display_name: displayName.trim() || null,
          password: password.trim() || null,
        };
        const r = await axios.post(`${API}/admin/users`, payload);
        setResult({
          email:    r.data.email,
          password: r.data.generated_password || password.trim(),
          generated: !!r.data.generated_password,
          user_id:  r.data.user_id,
        });
        toast.success(`User ${r.data.email} created`);
      } else {
        const payload = autoGen ? { generate: true } : { password: password.trim() };
        const r = await axios.post(`${API}/admin/users/${targetUser.id}/reset-password`, payload);
        setResult({
          email:    r.data.email,
          password: r.data.password,
          generated: r.data.generated,
        });
        toast.success('Password reset');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally { setSubmitting(false); }
  };

  const copy = () => {
    navigator.clipboard?.writeText(result?.password || '');
    toast.success('Password copied — share it out-of-band');
  };

  const finishAndClose = () => {
    onDone && onDone(result);
    close();
  };

  return createPortal((
    <div
      className="fixed inset-0 z-[140] bg-black/60 flex items-center justify-center p-4"
      data-testid="admin-user-form-modal"
      onClick={close}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-card border border-border shadow-2xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-2">
          <div className="h-9 w-9 rounded-xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
            {mode === 'create' ? <UserPlus className="w-4 h-4" /> : <KeyRound className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Admin tool</div>
            <h3 className="font-heading text-base font-extrabold text-foreground">
              {mode === 'create' ? 'Create a new user' : `Reset password for ${targetUser?.email || 'user'}`}
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              {mode === 'create'
                ? 'Bypass signup and provision an account directly. Leave the password blank to auto-generate a 12-char temporary one.'
                : 'Pick auto-generate (recommended) or set a specific password. The new password is shown once so you can share it privately.'}
            </p>
          </div>
          <button
            type="button" onClick={close}
            className="ml-auto h-7 w-7 rounded-full hover:bg-muted flex items-center justify-center text-muted-foreground"
            aria-label="Close" data-testid="admin-user-form-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {result ? (
          <div className="mt-4 rounded-xl border-2 border-emerald-500/40 bg-emerald-500/5 p-4">
            <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">
              {mode === 'create' ? 'Account ready' : 'Password reset'}
            </div>
            <div className="mt-1 text-sm">
              <div><span className="text-muted-foreground">Email:</span> <span className="font-semibold">{result.email}</span></div>
              <div className="mt-1 flex items-center gap-2">
                <span className="text-muted-foreground">Password:</span>
                <code className="font-mono bg-muted px-2 py-0.5 rounded" data-testid="admin-user-form-result-password">
                  {result.password}
                </code>
                <Button size="sm" variant="outline" className="rounded-full h-7" onClick={copy}>
                  <Copy className="w-3 h-3 mr-1" /> Copy
                </Button>
              </div>
              <div className="mt-2 text-[11px] text-muted-foreground">
                This is the ONLY time we'll show this. Copy it now and share via a secure channel.
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button className="rounded-full font-bold" onClick={finishAndClose} data-testid="admin-user-form-done">
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            {mode === 'create' && (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Email</label>
                  <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                         placeholder="user@example.com" data-testid="admin-user-form-email" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Display name</label>
                  <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)}
                         placeholder="Optional" data-testid="admin-user-form-name" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Role</label>
                  <select value={role} onChange={(e) => setRole(e.target.value)}
                          className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                          data-testid="admin-user-form-role">
                    <option value="free">Free member</option>
                    <option value="pro">Pro member</option>
                    <option value="moderator">Moderator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </>
            )}

            {mode === 'reset' && (
              <div className="space-y-2">
                <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${autoGen ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                  <input type="radio" name="reset-mode" checked={autoGen} onChange={() => setAutoGen(true)}
                         className="mt-0.5" data-testid="admin-user-form-mode-auto" />
                  <span>
                    <span className="font-semibold text-sm">Generate a random 12-char password</span>
                    <span className="block text-xs text-muted-foreground">Recommended. Copy-paste to the user via a secure channel.</span>
                  </span>
                </label>
                <label className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${!autoGen ? 'border-primary/40 bg-primary/5' : 'border-border'}`}>
                  <input type="radio" name="reset-mode" checked={!autoGen} onChange={() => setAutoGen(false)}
                         className="mt-0.5" data-testid="admin-user-form-mode-typed" />
                  <span>
                    <span className="font-semibold text-sm">Set a specific password</span>
                    <span className="block text-xs text-muted-foreground">You choose the value. Must be at least 6 characters.</span>
                  </span>
                </label>
              </div>
            )}

            {(mode === 'create' || !autoGen) && (
              <div>
                <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                  Password {mode === 'create' && '(optional — leave blank to auto-generate)'}
                </label>
                <div className="relative mt-1">
                  <Input type={showPw ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                         placeholder={mode === 'create' ? 'Blank = auto-generate' : 'At least 6 characters'}
                         data-testid="admin-user-form-password" required={mode === 'reset' && !autoGen} minLength={mode === 'reset' && !autoGen ? 6 : undefined} />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            <div className="pt-2 flex justify-end gap-2">
              <Button variant="outline" onClick={close} type="button" disabled={submitting} className="rounded-full font-bold">
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="rounded-full font-bold" data-testid="admin-user-form-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Working…</> : (
                  mode === 'create' ? <><UserPlus className="w-4 h-4 mr-2" /> Create user</> : <><ShieldCheck className="w-4 h-4 mr-2" /> Reset password</>
                )}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  ), document.body);
};

export default AdminUserFormModal;
